import { BaseObserver, LogLevels } from '@powersync/common';
import { PSInternalTable, rawPowerSyncControl, targetCheckpointRequestId } from './BucketStorageAdapter.js';
import { CrudEntryImpl } from './CrudEntry.js';
import { MAX_OP_ID } from '../../../constants.js';
/**
 * @internal
 */
export class SqliteBucketStorage extends BaseObserver {
    db;
    logger;
    updateListener;
    _clientId;
    constructor(db, logger) {
        super();
        this.db = db;
        this.logger = logger;
        this.updateListener = db.registerListener({
            tablesUpdated: ({ tables }) => {
                if (tables.includes(PSInternalTable.CRUD)) {
                    this.iterateListeners((l) => l.crudUpdate?.());
                }
            }
        });
    }
    async dispose() {
        this.updateListener?.();
    }
    async _getClientId() {
        const row = await this.db.get('SELECT powersync_client_id() as client_id');
        return row['client_id'];
    }
    getClientId() {
        if (this._clientId == null) {
            this._clientId = this._getClientId();
        }
        return this._clientId;
    }
    async updateLocalTarget(cb) {
        const sequenceBefore = await this.db.readTransaction(async (tx) => {
            const currentCheckpoint = await targetCheckpointRequestId(tx);
            if (currentCheckpoint != MAX_OP_ID)
                return;
            const rs = await tx.getOptional("SELECT seq FROM main.sqlite_sequence WHERE name = 'ps_crud'");
            return rs?.seq;
        });
        if (sequenceBefore == null) {
            // Nothing to update
            return false;
        }
        const opId = await cb();
        return this.writeTransaction(async (tx) => {
            const anyData = await tx.execute('SELECT 1 FROM ps_crud LIMIT 1');
            if (anyData.rows?.length) {
                // if isNotEmpty
                this.logger.log({
                    level: LogLevels.debug,
                    message: `New data uploaded since write checkpoint ${opId} - need new write checkpoint`
                });
                return false;
            }
            const { seq: seqAfter } = await tx.get("SELECT seq FROM main.sqlite_sequence WHERE name = 'ps_crud'");
            if (seqAfter != sequenceBefore) {
                this.logger.log({
                    level: LogLevels.debug,
                    message: `New data uploaded since write checpoint ${opId} - need new write checkpoint (sequence updated)`
                });
                // New crud data may have been uploaded since we got the checkpoint. Abort.
                return false;
            }
            this.logger.log({
                level: LogLevels.debug,
                message: `Updating target write checkpoint to ${opId}`
            });
            await targetCheckpointRequestId(tx, opId);
            return true;
        });
    }
    readCheckpointRequestId(variant, payload = null) {
        // This needs to run in a write transaction because some checkpoint request reads interact with sync client state
        // bound to the write connection.
        return this.db.writeTransaction(async (tx) => {
            return (await rawPowerSyncControl(tx, `${variant}_checkpoint_request_id`, payload));
        });
    }
    async nextCrudItem() {
        const next = await this.db.getOptional('SELECT * FROM ps_crud ORDER BY id ASC LIMIT 1');
        if (!next) {
            return;
        }
        return CrudEntryImpl.fromRow(next);
    }
    async hasCrud() {
        const anyData = await this.db.getOptional('SELECT 1 FROM ps_crud LIMIT 1');
        return !!anyData;
    }
    /**
     * Get a batch of objects to send to the server.
     * When the objects are successfully sent to the server, call .complete()
     */
    async getCrudBatch(limit = 100) {
        if (!(await this.hasCrud())) {
            return null;
        }
        const crudResult = await this.db.getAll('SELECT * FROM ps_crud ORDER BY id ASC LIMIT ?', [limit]);
        const all = [];
        for (const row of crudResult) {
            all.push(CrudEntryImpl.fromRow(row));
        }
        if (all.length === 0) {
            return null;
        }
        const last = all[all.length - 1];
        return {
            crud: all,
            haveMore: true,
            complete: async (writeCheckpoint) => {
                return this.handleCrudCheckpoint(last.clientId, writeCheckpoint);
            }
        };
    }
    handleCrudCheckpoint(lastClientId, writeCheckpoint) {
        return this.writeTransaction(async (tx) => {
            await tx.execute('DELETE FROM ps_crud WHERE id <= ?', [lastClientId]);
            if (writeCheckpoint) {
                const crudResult = await tx.execute('SELECT 1 FROM ps_crud LIMIT 1');
                if (crudResult.rows?.length) {
                    await targetCheckpointRequestId(tx, writeCheckpoint);
                }
            }
            else {
                await targetCheckpointRequestId(tx, MAX_OP_ID);
            }
        });
    }
    async writeTransaction(callback, options) {
        return this.db.writeTransaction(callback, options);
    }
    async control(op, payload) {
        return await this.writeTransaction(async (tx) => {
            return (await rawPowerSyncControl(tx, op, payload));
        });
    }
    async hasMigratedSubkeys() {
        const { r } = await this.db.get('SELECT EXISTS(SELECT * FROM ps_kv WHERE key = ?) as r', [
            SqliteBucketStorage._subkeyMigrationKey
        ]);
        return r != 0;
    }
    async migrateToFixedSubkeys() {
        await this.writeTransaction(async (tx) => {
            await tx.execute('UPDATE ps_oplog SET key = powersync_remove_duplicate_key_encoding(key);');
            await tx.execute('INSERT OR REPLACE INTO ps_kv (key, value) VALUES (?, ?);', [
                SqliteBucketStorage._subkeyMigrationKey,
                '1'
            ]);
        });
    }
    static _subkeyMigrationKey = 'powersync_js_migrated_subkeys';
}
//# sourceMappingURL=SqliteBucketStorage.js.map